import React from "react";
import { useKT } from "../../i18n/kt";
import { rupees } from "../../rates/rateModel";
import "../cashbook.scss";

/** Whole rupees only on the floor keypad; at most 7 digits (₹99,99,999). */
export const MAX_DIGITS = 7;

/** The next value after a key press: digits append, "back" removes one, no leading zeros. */
export const pressKey = (value: string, key: string, maxDigits = MAX_DIGITS): string => {
  if (key === "back") return value.slice(0, -1);
  if (!/^\d+$/.test(key)) return value;
  const next = (value === "0" ? "" : value) + key;
  const trimmed = next.replace(/^0+(?=\d)/, "");
  return trimmed.length > maxDigits ? value : trimmed;
};

type Props = {
  value: string;
  onChange: (v: string) => void;
  /** Visible label above the amount ("रकम", "सही रकम"). */
  label: string;
  /** id for the amount, so a <label> or aria-describedby can point at it. */
  id?: string;
  maxDigits?: number;
};

/**
 * Big number pad for amounts (spends, giving cash, a corrected amount). Phones keep the system keyboard
 * away, so the screen does not jump; a laptop can type digits and Backspace straight into it.
 */
const Keypad = ({ value, onChange, label, id = "cb-keypad", maxDigits = MAX_DIGITS }: Props) => {
  const { t } = useKT();
  const press = (k: string) => onChange(pressKey(value, k, maxDigits));
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      press(e.key);
    } else if (e.key === "Backspace") {
      e.preventDefault();
      press("back");
    }
  };
  return (
    <div className="cb-keypad">
      <div className="cb-keypad__label" id={`${id}-label`}>
        {label}
      </div>
      <div
        id={id}
        className={`cb-keypad__value${value ? "" : " is-empty"}`}
        role="textbox"
        tabIndex={0}
        aria-labelledby={`${id}-label`}
        aria-live="polite"
        onKeyDown={onKeyDown}
      >
        {value ? rupees(value) : "₹0"}
      </div>
      <div className="cb-keypad__keys">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9", "00", "0"].map((k) => (
          <button key={k} type="button" className="cb-keypad__key" onClick={() => press(k)}>
            {k}
          </button>
        ))}
        <button type="button" className="cb-keypad__key is-back" onClick={() => press("back")} aria-label={t("Backspace")}>
          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 5H9l-6 7 6 7h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1z" />
            <path d="M17 9l-6 6M11 9l6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Keypad;
